<script setup lang="ts">
/**
 * admin-kit — новый лейаут админки на UI-kit (HeroUI токены).
 * Слева collapsible-сайдбар, сверху топбар (поиск/тема/уведомления),
 * справа контекстный aside (useAdminAside). Подключается на странице через
 * definePageMeta({ layout: 'admin-kit' }). Старый layout 'admin' не затронут.
 */
import { useModalStore } from '@shared/model'
import { useAdminSupportAlerts } from '~/widgets/admin-shell'

// Глобальные оповещения чата поддержки: звук + тост о новом тикете/сообщении
// клиента с любой страницы админки (на странице чата — свои обработчики)
useAdminSupportAlerts()

// Состояние сворачивания хранится в cookie (а не localStorage), чтобы SSR
// отрендерил сайдбар в том же состоянии, в котором его гидрирует клиент.
// localStorage недоступен на сервере → расхождение гидрации (сайдбар «залипал»
// в полу-свёрнутом виде после перезагрузки со свёрнутой рейкой).
const collapsed = useCookie<boolean>('admin-kit-sidebar-collapsed', {
  default: () => false,
  sameSite: 'lax',
})
const mobileOpen = ref(false)
const asideMobileOpen = ref(false)
const modalStore = useModalStore()
</script>

<template>
  <!-- Админка не следует пресету скруглений витрины: настройка про то, как выглядит
       заведение для гостя, а не про рабочий инструмент. Возвращаем родные значения
       tailwind, иначе выбор «квадратные» заодно обрубал бы углы всей админке.
       Превью в разделе «Внешний вид» ставит переменные на свою обёртку и не страдает. -->
  <div
    data-testid="admin-shell"
    class="h-screen flex gap-3 p-3 overflow-hidden bg-hui-background text-hui-foreground font-main antialiased"
    style="--app-radius-card: 1.5rem; --app-radius-media: 1rem; --app-radius-control: 9999px"
  >
    <AdminSidebar :collapsed="collapsed" :mobile-open="mobileOpen" @close="mobileOpen = false" />

    <!-- Затемнение под мобильным сайдбаром -->
    <Transition enter-active-class="transition-opacity duration-200" enter-from-class="opacity-0" leave-active-class="transition-opacity duration-200" leave-to-class="opacity-0">
      <div v-if="mobileOpen" class="fixed inset-0 z-40 bg-black/40 lg:hidden" @click="mobileOpen = false" />
    </Transition>

    <div class="flex-1 flex flex-col min-w-0 gap-3">
      <AdminTopbar
        :aside-available="true"
        @toggle-mobile="mobileOpen = !mobileOpen"
        @toggle-sidebar="collapsed = !collapsed"
        @toggle-aside-mobile="asideMobileOpen = !asideMobileOpen"
      />

      <div class="flex-1 flex gap-3 min-h-0 overflow-hidden">
        <main class="flex-1 min-w-0 overflow-y-auto">
          <div class="px-4 lg:px-6 min-h-full">
            <slot />
          </div>
        </main>
        <AdminAside :mobile-open="asideMobileOpen" @close="asideMobileOpen = false" />
      </div>
    </div>

    <!-- Затемнение под мобильным правым aside -->
    <Transition enter-active-class="transition-opacity duration-200" enter-from-class="opacity-0" leave-active-class="transition-opacity duration-200" leave-to-class="opacity-0">
      <div v-if="asideMobileOpen" class="fixed inset-0 z-40 bg-black/40 xl:hidden" @click="asideMobileOpen = false" />
    </Transition>

    <UiKitToaster placement="top-right" />
    <DynamicModal v-if="modalStore.isOpen" @close="modalStore.close" />
  </div>
</template>
